<template>
  <div
      class="scroll-fade-container"
      @mouseleave="onContainerMouseLeave"
  >
    <div
        ref="scrollContainer"
        class="scroll-container"
        @scroll="updateFadeState"
    >
      <div class="content-wrapper">
        <slot/>
      </div>
    </div>
    <div
        v-show="showLeftFade"
        class="fade-layer left-fade"
        :style="{
          width: faderWidth,
          pointerEvents: hoverScroll ? 'all' : 'none',
          background: `linear-gradient(
              to right,
              ${faderColor + 'FF'} 20%,
              ${faderColor + '00'} 100%
          )`
        }"
        v-if="faderLeft"
        @mouseover="onLeftHover"
        @mouseleave="onSideMouseleave"
    />
    <div
        v-show="showRightFade"
        class="fade-layer right-fade"
        :style="{
          width: faderWidth,
          pointerEvents: hoverScroll ? 'all' : 'none',
          background: `linear-gradient(
              to left,
              ${faderColor + 'FF'} 20%,
              ${faderColor + '00'} 100%
          )`
        }"
        v-if="faderRight"
        @mouseover="onRightHover"
        @mouseleave="onSideMouseleave"
    />
  </div>
</template>

<script lang="ts" setup>
import {onBeforeUnmount, onMounted, ref} from 'vue';
import {useQuasar} from "quasar";
import {useDebugComponent} from "@/core/composable/useDebugComponent";

export type ScrollHorizontalApi = {
  scrollToElement: (element: HTMLElement, options?: {
    behavior?: 'auto' | 'smooth';
    block?: 'start' | 'center' | 'end' | 'nearest';
    inline?: 'start' | 'center' | 'end' | 'nearest';
  }) => void
  scrollToStart: (options?: { behavior?: 'auto' | 'smooth' }) => void
  scrollToEnd: (options?: { behavior?: 'auto' | 'smooth' }) => void
}

const props = withDefaults(defineProps<{
  faderLeft?: boolean
  faderRight?: boolean
  faderWidth?: string
  faderColor?: string
  hoverScroll?: boolean
  hoverScrollBreakpoint?: 'xs' | 'sm' | 'md' | 'lg',
  scrollSpeed?: number
}>(), {
  faderWidth: '15px',
  faderLeft: true,
  faderRight: true,
  faderColor: '#FFFFFF',
  hoverScroll: false,
  hoverScrollBreakpoint: 'md',
  scrollSpeed: 3
})


const $q = useQuasar()
const {faderWidth, hoverScroll, hoverScrollBreakpoint} = props

const scrollContainer = ref<HTMLElement | null>(null);
const showLeftFade = ref(false);
const showRightFade = ref(false);
let resizeObserver: ResizeObserver | null = null;

const autoscrollDir = ref<'left' | 'right' | null>(null);
let animationFrameId: number | null = null;

const updateFadeState = () => {
  if (!scrollContainer.value) return;

  const {scrollLeft, clientWidth, scrollWidth} = scrollContainer.value;
  const isAtStart = scrollLeft <= 0;
  const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10;

  showLeftFade.value = !isAtStart;
  showRightFade.value = !isAtEnd;
};

const startAutoScroll = () => {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
  }

  const scroll = () => {
    if (!scrollContainer.value || !autoscrollDir.value) {
      return;
    }

    const {scrollLeft, clientWidth, scrollWidth} = scrollContainer.value;
    const scrollStep = props.scrollSpeed;

    // Проверяем достижение границ
    const isAtStart = scrollLeft <= 0;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth;

    if (autoscrollDir.value === 'left' && !isAtStart) {
      scrollContainer.value.scrollLeft -= scrollStep;
    } else if (autoscrollDir.value === 'right' && !isAtEnd) {
      scrollContainer.value.scrollLeft += scrollStep;
    } else {
      // Достигли границы - останавливаем скролл
      autoscrollDir.value = null;
      return;
    }

    // Продолжаем скроллить
    animationFrameId = requestAnimationFrame(scroll);
  };

  animationFrameId = requestAnimationFrame(scroll);
};

const stopAutoScroll = () => {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
};

// Публичный метод для скролла к элементу
const scrollToElement: ScrollHorizontalApi['scrollToElement'] = (element, options = {}) => {

  if (!scrollContainer.value || !element) {
    console.warn('Scroll container or target element not found');
    return;
  }

  // Проверяем, что элемент находится внутри скролл-контейнера
  if (!scrollContainer.value.contains(element)) {
    console.warn('Target element is not a child of scroll container');
    return;
  }

  const containerRect = scrollContainer.value.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  // Вычисляем позицию элемента относительно контейнера
  const elementLeftRelative = elementRect.left - containerRect.left;
  const elementRightRelative = elementRect.right - containerRect.left;

  // Текущая прокрутка
  const currentScroll = scrollContainer.value.scrollLeft;

  // Ширина видимой области
  const containerWidth = containerRect.width;

  let targetScroll = currentScroll;

  // Определяем целевое положение скролла в зависимости от опций
  const {behavior = 'smooth', block = 'nearest', inline = 'nearest'} = options;

  if (block === 'start' || inline === 'start') {
    // Выравниваем по левому краю
    targetScroll = currentScroll + elementLeftRelative;
  } else if (block === 'end' || inline === 'end') {
    // Выравниваем по правому краю
    targetScroll = currentScroll + elementRightRelative - containerWidth;
  } else if (block === 'center' || inline === 'center') {
    // Выравниваем по центру
    const elementCenter = (elementRect.left + elementRect.right) / 2;
    const containerCenter = containerRect.left + containerWidth / 2;
    targetScroll = currentScroll + (elementCenter - containerCenter);
  } else {
    // 'nearest' - скроллим только если элемент не виден полностью
    if (elementLeftRelative < 0) {
      // Элемент слева за границей
      targetScroll = currentScroll + elementLeftRelative;
    } else if (elementRightRelative > containerWidth) {
      // Элемент справа за границей
      targetScroll = currentScroll + elementRightRelative - containerWidth;
    } else {
      // Элемент полностью виден - не скроллим
      return;
    }
  }

  // Обеспечиваем, чтобы скролл не выходил за границы
  const maxScroll = scrollContainer.value.scrollWidth - containerWidth;
  targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));

  // Выполняем скролл
  scrollContainer.value.scrollTo({
    left: targetScroll,
    behavior
  });
}

// Публичный метод для скролла к началу
const scrollToStart: ScrollHorizontalApi['scrollToStart'] = (options = {}) => {
  if (!scrollContainer.value) return;
  scrollContainer.value.scrollTo({
    left: 0,
    behavior: options.behavior || 'smooth'
  });
};

// Публичный метод для скролла к концу
const scrollToEnd: ScrollHorizontalApi['scrollToEnd'] = (options = {}) => {
  if (!scrollContainer.value) return;

  const maxScroll = scrollContainer.value.scrollWidth - scrollContainer.value.clientWidth;
  scrollContainer.value.scrollTo({
    left: maxScroll,
    behavior: options.behavior || 'smooth'
  });
};

onMounted(() => {
  if (scrollContainer.value) {
    resizeObserver = new ResizeObserver(updateFadeState);
    resizeObserver.observe(scrollContainer.value);
    updateFadeState();
  }
});

onBeforeUnmount(() => {
  stopAutoScroll();
  if (resizeObserver && scrollContainer.value) {
    resizeObserver.unobserve(scrollContainer.value);
  }
});

const startScrollOnHover = (direction: 'left' | 'right') => {
  if (!props.hoverScroll || !scrollContainer.value) return;

  const {scrollLeft, clientWidth, scrollWidth} = scrollContainer.value;

  if (direction === 'left' && scrollLeft <= 0) return;
  if (direction === 'right' && scrollLeft + clientWidth >= scrollWidth) return;

  autoscrollDir.value = direction;
  startAutoScroll();
};

const onSideMouseleave = () => {
  if (hoverScroll && $q.screen.gt[hoverScrollBreakpoint]) {
    autoscrollDir.value = null;
    stopAutoScroll();
  }
}

const onLeftHover = () => {
  if (hoverScroll && $q.screen.gt[hoverScrollBreakpoint]) {
    startScrollOnHover('left');
  }
}

const onRightHover = () => {
  if (hoverScroll && $q.screen.gt[hoverScrollBreakpoint]) {
    startScrollOnHover('right');
  }
}

const onContainerMouseLeave = () => {
  autoscrollDir.value = null;
  stopAutoScroll();
}


defineExpose<ScrollHorizontalApi>({
  scrollToElement,
  scrollToStart,
  scrollToEnd,
});

// Debug композабл для отслеживания ререндеров
useDebugComponent('ScrollHorizontal', { collectRenders: true });

</script>

<style scoped>
.scroll-fade-container {
  position: relative;
  width: 100%;
  max-width: 100%;
}

.scroll-container {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  white-space: nowrap;
}

.scroll-container::-webkit-scrollbar {
  display: none;
}

.content-wrapper {
  display: inline-block;
  min-width: 100%;
}

.fade-layer {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 40px;
  pointer-events: auto;
  z-index: 1;
}

.left-fade {
  left: 0;
}

.right-fade {
  right: 0;
}

@media (hover: hover) {
  .scroll-container {
    overflow-x: hidden;
  }

  .scroll-container:hover {
    overflow-x: auto;
  }
}
</style>
